"use client";

import { useState } from "react";
import {
    ArrowLeft,
    ArrowRight,
    Check,
    ImageOff,
    Layers,
    LoaderCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGetPublicAvatars } from "../api/useGetPublicAvatars";
import { useGetPublicAvatarLooks } from "../api/useGetPublicAvatarLooks";
import { hasRenderableEngine } from "../types";
import { getDirectionFromText, t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/lib/utils";

interface PublicAvatarGroup {
    id: string;
    name: string;
    preview_image_url?: string | null;
    avatar_type?: string;
    default_voice_id?: string | null;
    looks_count?: number;
}

export interface PublicLookSelection {
    lookId: string;
    name: string;
    previewImageUrl?: string | null;
    supportedEngines?: ("avatar_iv" | "avatar_v")[];
}

interface Props {
    onSelect?: (look: PublicLookSelection) => void;
    /** Look id currently applied to the active scene, if any. */
    selectedLookId?: string;
    /** Grid column count. 2 for the editor sidebar (default), 3 for the funnel. */
    columns?: 2 | 3;
}

export default function PublicAvatarBrowser({
    onSelect,
    selectedLookId,
    columns = 2,
}: Props) {
    const { locale, dir } = useLocale();
    const gridCols = columns === 3 ? "grid-cols-3" : "grid-cols-2";
    const { data: groups, isLoading } = useGetPublicAvatars();

    const [openGroup, setOpenGroup] = useState<PublicAvatarGroup | null>(null);
    const { data: looks, isLoading: looksLoading } = useGetPublicAvatarLooks(
        openGroup?.id
    );

    if (isLoading) {
        return (
            <div className="flex justify-center py-8">
                <LoaderCircle className="size-4 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (openGroup) {
        const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;
        // Only looks HeyGen can render with Avatar IV/V are usable downstream.
        const renderableLooks = looks?.filter((l) =>
            hasRenderableEngine(l.supported_api_engines)
        );
        return (
            <div className="flex flex-col gap-3">
                <div className="-mt-1 mb-1 flex-row flex items-center gap-2">
                    <Button
                        size="icon"
                        variant="ghost"
                        className="size-7"
                        onClick={() => setOpenGroup(null)}
                    >
                        <BackIcon className="size-4" />
                    </Button>
                    <div className="flex flex-col min-w-0">
                        <p className="text-sm font-medium truncate">
                            {openGroup.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {t("avatars.sidebar.looks.subtitle", locale)}
                        </p>
                    </div>
                </div>

                {looksLoading ? (
                    <div className="flex justify-center py-8">
                        <LoaderCircle className="size-4 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <div className={cn("grid gap-2", gridCols)}>
                        {renderableLooks?.map((l) => {
                            const isApplied = l.id === selectedLookId;
                            return (
                                <button
                                    key={l.id}
                                    type="button"
                                    aria-pressed={isApplied}
                                    title={
                                        isApplied
                                            ? t("avatars.look.applied", locale)
                                            : undefined
                                    }
                                    onClick={() => {
                                        if (isApplied) return;
                                        onSelect?.({
                                            lookId: l.id,
                                            name: l.name,
                                            previewImageUrl:
                                                l.preview_image_url,
                                            supportedEngines:
                                                l.supported_api_engines,
                                        });
                                    }}
                                    className={cn(
                                        "group relative rounded-lg border overflow-hidden text-left",
                                        isApplied
                                            ? "ring-2 ring-primary cursor-default"
                                            : "cursor-pointer hover:ring-2 hover:ring-ring/50"
                                    )}
                                >
                                    <div className="aspect-square bg-muted relative">
                                        {l.preview_image_url && (
                                            // eslint-disable-next-line @next/next/no-img-element
                                            <img
                                                src={l.preview_image_url}
                                                alt={l.name}
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                        {isApplied && (
                                            <span className="absolute top-1 start-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none bg-primary text-primary-foreground backdrop-blur-sm shadow-sm">
                                                <Check className="size-2.5" />
                                                {t(
                                                    "avatars.look.applied",
                                                    locale
                                                )}
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        dir={getDirectionFromText(l.name)}
                                        className="p-1.5 w-full text-[11px] truncate text-start"
                                    >
                                        {l.name}
                                    </div>
                                </button>
                            );
                        })}
                        {renderableLooks && renderableLooks.length === 0 && (
                            <div className="col-span-full flex flex-col items-center justify-center gap-3 py-16 text-center text-sm text-muted-foreground">
                                <ImageOff className="size-8 opacity-50" />
                                <p>{t("avatars.public.no-looks", locale)}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={cn("grid gap-2", gridCols)}>
            {(groups as PublicAvatarGroup[] | undefined)?.map((a) => (
                <button
                    key={a.id}
                    type="button"
                    onClick={() => setOpenGroup(a)}
                    className="group relative rounded-lg border overflow-hidden hover:ring-2 hover:ring-ring/50 text-left cursor-pointer"
                >
                    <div className="aspect-square bg-muted relative">
                        {a.preview_image_url && (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                                src={a.preview_image_url}
                                alt={a.name}
                                className="w-full h-full object-cover"
                            />
                        )}
                        {/* HeyGen's `looks_count` is unreliable (often wildly
                            inflated vs. what GET /v3/avatars/looks returns),
                            so we show only an icon hint, not a number. */}
                        <div className="absolute bottom-1 end-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white bg-black/60 backdrop-blur-sm ring-1 ring-white/15 shadow-sm">
                            <Layers className="size-3" />
                        </div>
                    </div>
                    <div
                        dir={getDirectionFromText(a.name)}
                        className="p-1.5 w-full text-[11px] truncate text-start"
                    >
                        {a.name}
                    </div>
                </button>
            ))}
        </div>
    );
}
